import {
  Directive,
  Input,
  Renderer2,
  ViewContainerRef,
  TemplateRef,
  OnDestroy,
  EmbeddedViewRef,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { filter, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { UiRoleManagerService, StoreService } from '@services';
import { ERoleMode } from '@enums';

import { IBitfUiRoleManagerConfig } from './bitf-ui-role-manager.interface';

@Directive({
  selector: '[bitfUiRoleManager]',
})
export class BitfUiRoleManagerDirective implements OnDestroy, OnChanges {
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('bitfUiRoleManager')
  config: IBitfUiRoleManagerConfig = {};
  nativeElement: HTMLElement;

  subscription: Subscription = new Subscription();
  embededViewRef: EmbeddedViewRef<any>;

  constructor(
    private viewRef: ViewContainerRef,
    private templateRef: TemplateRef<any>,
    private renderer: Renderer2,
    private uiRoleManagerService: UiRoleManagerService,
    private storeService: StoreService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.config.firstChange) {
      this.createComponent();
      this.listenForStoreActions();
      this.mutateView();
    } else if (changes?.config) {
      this.mutateView();
    }
  }

  private mutateView() {
    if (!this.config) {
      return;
    }
    const canI = this.config.inverted
      ? !this.uiRoleManagerService.canI(this.config.action, this.config.data)
      : this.uiRoleManagerService.canI(this.config.action, this.config.data);

    switch (this.config.mode || ERoleMode.HIDDEN) {
      case ERoleMode.HIDDEN:
        if (canI) {
          this.createEmbeddedView();
          this.setVisibility('visible');
        } else if (this.embededViewRef) {
          this.embededViewRef.destroy();
          this.embededViewRef = undefined;
        }
        break;
      case ERoleMode.INVISIBLE:
        this.createEmbeddedView();
        if (!this.isNativeElementReal()) {
          return;
        }
        setTimeout(() => {
          if (canI) {
            this.setVisibility('visible');
          } else if (this.embededViewRef) {
            this.setVisibility('hidden');
          }
        }, 0);
        break;
      case ERoleMode.DISABLED:
        this.createEmbeddedView();
        if (!this.isNativeElementReal()) {
          return;
        }
        setTimeout(() => {
          if (!canI) {
            this.renderer.setAttribute(this.nativeElement, 'disabled', 'true');
          } else {
            this.renderer.removeAttribute(this.nativeElement, 'disabled');
          }
          this.setVisibility('visible');
        }, 0);
        break;
      case ERoleMode.READ_ONLY:
        this.createEmbeddedView();
        if (!this.isNativeElementReal()) {
          return;
        }
        setTimeout(() => {
          if (!canI) {
            this.renderer.setAttribute(this.nativeElement, 'readonly', 'true');
          } else {
            this.renderer.removeAttribute(this.nativeElement, 'readonly');
          }
          this.setVisibility('visible');
        }, 0);
        break;
    }
  }

  private createEmbeddedView() {
    const viewContainerRef: any = this.createComponent();
    this.nativeElement = viewContainerRef.rootNodes[0];
    if (this.isNativeElementReal()) {
      this.renderer.addClass(this.nativeElement, 'uiRolesManager');
      this.setVisibility('hidden');
    }
  }

  private setVisibility(state: string) {
    if (this.isNativeElementReal()) {
      this.renderer.setStyle(this.nativeElement, 'visibility', state);
    }
  }

  private createComponent(): any {
    if (!this.embededViewRef) {
      this.embededViewRef = this.viewRef.createEmbeddedView(this.templateRef, {});
    }

    return this.embededViewRef;
  }

  private isNativeElementReal(): boolean {
    return this.nativeElement.innerHTML !== undefined;
  }

  private listenForStoreActions() {
    if (!this?.config?.storeActionsToListen?.length) {
      return;
    }

    this.subscription.add(
      this.storeService.store$
        .pipe(
          filter(store => this.config.storeActionsToListen.includes(store.action)),
          tap(() => this.mutateView())
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
